import React, { useEffect, useMemo, useState } from "react";
import { Box, Accordion, AccordionDetails, AccordionSummary, Grid, MenuItem, TextField, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import { alpha, useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import { useSelector } from "react-redux";

import {
  AttributeCardSelector,
  BasicDetailsStep,
  PermissionToggleCard,
  PreferencesStep,
  StickyModalFooter,
  WizardModalLayout,
} from "./wizard/WizardComponents";
import {
  defaultPreferences,
  defaultUserPermissions,
  LANGUAGES,
  normalizeUserId,
  TIMEZONES,
  UNITS,
  userPermissionsSchema,
} from "./permissionsDefaults";
import { useCreateUserMutation, useEditUserMutation } from "../../redux/admin/adminApi";

const ATTRIBUTE_OPTIONS = [
  { key: "notificationTokens", label: "Notification Tokens", type: "number" },
  { key: "currency", label: "Currency", type: "select", options: ["INR", "USD"] },
  { key: "unitPrice", label: "Unit Price", type: "number" },
  { key: "managerEmailAddress", label: "Manager Email", type: "email" },
  { key: "managerContactNumber", label: "Manager Contact", type: "tel" },
  { key: "remarks", label: "Remarks", type: "text" },
  { key: "roleAttribute", label: "Role", type: "select", options: ["Parents", "Subuser", "Relay hide"] },
  { key: "video", label: "Video", type: "checkbox" },
];

const ATTRIBUTE_MAP = ATTRIBUTE_OPTIONS.reduce((acc, item) => {
  acc[item.key] = item;
  return acc;
}, {});

const buildParentAllowedForUserSchema = (currentUser) => {
  if (currentUser?.permissions && typeof currentUser.permissions === "object") {
    return currentUser.permissions;
  }

  return {
    user: {
      hideValidity: true,
    },
  };
};

const clampBySchema = (schema, parentAllowed, selected) => {
  const out = structuredClone(selected || {});
  schema.forEach((group) => {
    out[group.key] = out[group.key] ?? {};
    const parentGroup = parentAllowed?.[group.key] || {};
    group.items.forEach((item) => {
      const isAllowed = parentGroup?.[item.key] === true;
      out[group.key][item.key] = isAllowed ? Boolean(out[group.key]?.[item.key]) : false;
    });
  });
  return out;
};

const buildGeneratedUserEmail = (form, existingEmail) => {
  if (existingEmail) return existingEmail;
  const seed = normalizeUserId(form.uniqueId || form.username || form.mobileNo || "user");
  return `${seed || "user"}-${Date.now()}@user.local`;
};

const toUserFormState = (user) => {
  if (!user) {
    return {
      username: "",
      email: "",
      mobileNo: "",
      password: "",
      uniqueId: "",
      preferences: structuredClone(defaultPreferences),
      permissions: structuredClone(defaultUserPermissions),
      attributes: [],
    };
  }

  return {
    username: user.username || "",
    email: user.email || "",
    mobileNo: user.mobileNo || "",
    password: "",
    uniqueId: user.uniqueId || "",
    preferences: {
      ...defaultPreferences,
      ...(user.preferences || {}),
      timeZone:
        user.preferences?.timeZone ||
        user.preferences?.timezone ||
        defaultPreferences.timeZone,
      language: user.preferences?.language || defaultPreferences.language,
    },
    permissions: user.permissions
      ? structuredClone(user.permissions)
      : structuredClone(defaultUserPermissions),
    attributes: Array.isArray(user.attributes) ? structuredClone(user.attributes) : [],
  };
};

const getBasicStepErrors = (form, mode) => {
  const errors = {};
  if (!form.username?.trim()) errors.username = "Name is required";
  if (!form.uniqueId?.trim()) errors.uniqueId = "User ID is required";
  if (!form.mobileNo?.trim()) errors.mobileNo = "Mobile is required";
  if (mode !== "edit" && !form.password?.trim()) errors.password = "Password is required";
  return errors;
};

const createPreferenceFields = () => [
  { path: "preferences.speedUnit", label: "Speed Unit", options: UNITS.speed, required: true },
  { path: "preferences.distanceUnit", label: "Distance Unit", options: UNITS.distance, required: true },
  { path: "preferences.altitudeUnit", label: "Altitude Unit", options: UNITS.altitude, required: true },
  { path: "preferences.volumeUnit", label: "Volume Unit", options: UNITS.volume, required: true },
  { path: "preferences.timeZone", label: "Time Zone", options: TIMEZONES, required: true },
  { path: "preferences.language", label: "Language", options: LANGUAGES, required: true },
];

export default function AddUserModal({
  open,
  onClose,
  onSuccess,
  mode = "create",
  initialData = null,
  forcedRole = "user",
  title,
  subtitle,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [createUser, { isLoading }] = useCreateUserMutation();
  const [editUser, { isLoading: isEditing }] = useEditUserMutation();
  const currentUser = useSelector((state) => state?.auth?.user);
  const parentAllowed = useMemo(
    () => buildParentAllowedForUserSchema(currentUser),
    [currentUser]
  );

  const [error, setError] = useState("");
  const [currentStep, setCurrentStep] = useState(0);
  const [showStepErrors, setShowStepErrors] = useState(false);
  const [form, setForm] = useState(() => toUserFormState(initialData));
  const [selectedAttributeKey, setSelectedAttributeKey] = useState("");
  const [draftAttributeValue, setDraftAttributeValue] = useState("");
  const [advancedExpanded, setAdvancedExpanded] = useState(false);
  const effectiveRole = initialData?.role || forcedRole || "user";

  useEffect(() => {
    if (!open) return;
    setForm(toUserFormState(initialData));
    setError("");
    setCurrentStep(0);
    setShowStepErrors(false);
    setSelectedAttributeKey("");
    setDraftAttributeValue("");
    setAdvancedExpanded(false);
  }, [open, initialData]);

  const steps = [
    { key: "basic", title: "Basic Details", description: "Account information" },
    { key: "preferences", title: "Preferences", description: "Default settings" },
    { key: "permissions", title: "Permissions", description: "Access and controls" },
  ];

  const onChange = (path, value) => {
    setError("");
    if (path === "uniqueId") value = normalizeUserId(value);
    setForm((prev) => {
      const keys = path.split(".");
      const next = structuredClone(prev);
      let cursor = next;
      for (let index = 0; index < keys.length - 1; index += 1) {
        cursor[keys[index]] = cursor[keys[index]] ?? {};
        cursor = cursor[keys[index]];
      }
      cursor[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const usedAttributeKeys = (form.attributes || []).map((attr) => attr.key);
  const availableAttributeOptions = ATTRIBUTE_OPTIONS.filter(
    (item) => !usedAttributeKeys.includes(item.key)
  );

  const selectAttribute = (key) => {
    const config = ATTRIBUTE_MAP[key];
    setSelectedAttributeKey(key);
    setDraftAttributeValue(config?.type === "checkbox" ? false : "");
  };

  const addAttribute = () => {
    if (!selectedAttributeKey) return;
    if (usedAttributeKeys.includes(selectedAttributeKey)) return;
    const selectedConfig = ATTRIBUTE_MAP[selectedAttributeKey];
    if (!selectedConfig) return;

    setForm((prev) => ({
      ...prev,
      attributes: [
        ...(prev.attributes || []),
        {
          key: selectedConfig.key,
          label: selectedConfig.label,
          type: selectedConfig.type,
          value: selectedConfig.type === "checkbox" ? Boolean(draftAttributeValue) : draftAttributeValue,
        },
      ],
    }));
    setSelectedAttributeKey("");
    setDraftAttributeValue("");
  };

  const updateAttribute = (index, value) => {
    setForm((prev) => {
      const nextAttributes = [...(prev.attributes || [])];
      nextAttributes[index] = { ...nextAttributes[index], value };
      return { ...prev, attributes: nextAttributes };
    });
  };

  const removeAttribute = (index) => {
    setForm((prev) => ({
      ...prev,
      attributes: (prev.attributes || []).filter((_, attrIndex) => attrIndex !== index),
    }));
  };

  const togglePermission = (groupKey, itemKey) => {
    if (parentAllowed?.[groupKey]?.[itemKey] !== true) return;
    setForm((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [groupKey]: {
          ...prev.permissions?.[groupKey],
          [itemKey]: !prev.permissions?.[groupKey]?.[itemKey],
        },
      },
    }));
  };

  const basicErrors = useMemo(() => getBasicStepErrors(form, mode), [form, mode]);
  const visibleBasicErrors = showStepErrors && currentStep === 0 ? basicErrors : {};

  const handleNext = () => {
    if (currentStep === 0 && Object.keys(basicErrors).length) {
      setShowStepErrors(true);
      return;
    }
    setShowStepErrors(false);
    setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleSubmit = async () => {
    setError("");
    if (Object.keys(basicErrors).length) {
      setCurrentStep(0);
      setShowStepErrors(true);
      return;
    }

    const clampedPermissions = clampBySchema(
      userPermissionsSchema,
      parentAllowed,
      form.permissions
    );

    const payload = {
      username: form.username.trim(),
      email: buildGeneratedUserEmail(form, initialData?.email || form.email).toLowerCase(),
      mobileNo: String(form.mobileNo || "").trim(),
      ...(form.password?.trim() ? { password: form.password } : {}),
      role: effectiveRole,
      uniqueId: form.uniqueId ? normalizeUserId(form.uniqueId) : undefined,
      preferences: form.preferences,
      permissions: clampedPermissions,
      attributes: form.attributes,
    };

    try {
      const response =
        mode === "edit" && initialData?._id
          ? await editUser({ userId: initialData._id, ...payload }).unwrap()
          : await createUser(payload).unwrap();

      onSuccess?.(response);
      onClose?.();
    } catch (submitError) {
      setError(
        submitError?.data?.msg ||
          submitError?.message ||
          (mode === "edit" ? "Failed to update user" : "Failed to create user")
      );
    }
  };

  const detailFields = [
    { path: "username", label: "Name", required: true },
    {
      path: "uniqueId",
      label: "User ID",
      required: true,
      helperText: "No spaces. Only a-z, 0-9, dot, underscore, dash.",
    },
    { path: "mobileNo", label: "Mobile", required: true, inputProps: { inputMode: "numeric" } },
    {
      path: "password",
      label: "Password",
      type: "password",
      required: mode !== "edit",
      helperText: mode === "edit" ? "Leave blank to keep current password." : "Use a secure password.",
      md: 12,
    },
  ];

  const renderPermissionsStep = () => (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <PermissionToggleCard
            title="User Access"
            description="Keep current user-level permission rules exactly as configured."
            items={userPermissionsSchema.flatMap((group) =>
              group.items.map((item) => ({
                ...item,
                groupKey: group.key,
              }))
            )}
            values={form.permissions}
            parentAllowed={parentAllowed}
            onToggle={togglePermission}
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <Accordion
            expanded={advancedExpanded}
            onChange={(_, expanded) => setAdvancedExpanded(expanded)}
            sx={{
              borderRadius: 4,
              border: "1px solid #e5e7eb",
              boxShadow: "none",
              backgroundColor: alpha(theme.palette.secondary.light, 0.12),
              "&::before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box>
                <Typography sx={{ fontWeight: 800 }}>Advanced Settings</Typography>
                <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                  Additional access details stay collapsed by default.
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography sx={{ color: "#64748b", fontSize: 14 }}>
                This user flow only exposes the existing `hideValidity` permission. No other permission payloads are changed.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>

      <AttributeCardSelector
        options={ATTRIBUTE_OPTIONS}
        attributeDefinitions={ATTRIBUTE_OPTIONS}
        disabledKeys={usedAttributeKeys}
        selectedKey={selectedAttributeKey}
        onSelectKey={selectAttribute}
        draftValue={draftAttributeValue}
        onDraftValueChange={setDraftAttributeValue}
        onAdd={addAttribute}
        attributes={form.attributes || []}
        onUpdateAttribute={updateAttribute}
        onRemoveAttribute={removeAttribute}
      />
    </Box>
  );

  return (
    <WizardModalLayout
      open={open}
      onClose={onClose}
      icon={PersonAddAlt1OutlinedIcon}
      title={title || (mode === "edit" ? "Edit User" : "Add User")}
      subtitle={
        subtitle ||
        (mode === "edit"
          ? "Update user information in 3 simple steps."
          : "Create a new user account in 3 simple steps.")
      }
      steps={steps}
      currentStep={currentStep}
      onStepChange={setCurrentStep}
      error={error}
      helpTitle="Need Help?"
      helpText="Fill the basic details first. You can review preferences and permissions before saving."
      fullScreen={fullScreen}
      maxWidth="lg"
      footer={
        <StickyModalFooter
          currentStep={currentStep}
          steps={steps}
          loading={isLoading || isEditing}
          onBack={() => setCurrentStep((prev) => Math.max(prev - 1, 0))}
          onCancel={onClose}
          onNext={handleNext}
          onSubmit={handleSubmit}
          submitLabel={mode === "edit" ? "Save User" : "Create User"}
        />
      }
    >
      {currentStep === 0 ? (
        <BasicDetailsStep
          title="Account Information"
          description="Enter the basic information for the user account."
          form={form}
          onChange={onChange}
          fields={detailFields}
          errors={visibleBasicErrors}
          tip="User ID helper and existing API payload behavior remain unchanged."
        />
      ) : null}

      {currentStep === 1 ? (
        <PreferencesStep
          form={form}
          onChange={onChange}
          errors={{}}
          fields={createPreferenceFields()}
          note="These preferences will be used as default settings. They still map to the same API payload."
        />
      ) : null}

      {currentStep === 2 ? renderPermissionsStep() : null}
    </WizardModalLayout>
  );
}
